// ============================================================
// ExpressRequestResponse.jsx
//
// A branded, responsive course content page for the
// "Request & Response with Async Logic" lesson.
//
// Author: ProDev Linux Series (Backend Branch)
// Features: Async/Await patterns, simulated DB interactions,
//           error handling, and navigation bridge to Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import SwapHorizIcon         from "@mui/icons-material/SwapHoriz";
import TimerIcon             from "@mui/icons-material/Timer";
import BugReportIcon          from "@mui/icons-material/BugReport";
import TerminalIcon          from "@mui/icons-material/Terminal";
import LightbulbIcon         from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import StorageIcon           from "@mui/icons-material/Storage";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressRequestResponse() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Asynchronous Logic · Module 06"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Request <span>& Response</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Handling requests in Express involves navigating asynchronous lifecycles. At ProDev, we utilize Async/Await to manage database interactions while ensuring clean error handling for a resilient API.
                    </Typography>
                </Box>

                {/* 1. ASYNC FETCH SIMULATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TimerIcon />} label="1. Simulated Async Logic" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Most real-world operations (DB queries, API calls) are asynchronous. We use Promises to simulate these "wait" periods.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const getUserFromDb = async (userId) => {", comment: "Async DB Simulator" },
                        { cmd: "  return new Promise((resolve, reject) => {", comment: "" },
                        { cmd: "    setTimeout(() => {", comment: "Simulate network delay" },
                        { cmd: "      const users = { '1': { name: 'Alice' } };", comment: "" },
                        { cmd: "      users[userId] ? resolve(users[userId]) : reject(new Error('Not Found'));", comment: "" },
                        { cmd: "    }, 500);", comment: "" },
                        { cmd: "  });", comment: "" },
                        { cmd: "};", comment: "" },
                    ]} />
                </Box>

                {/* 2. ROUTE HANDLERS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SwapHorizIcon />} label="2. GET & POST Implementation" />
                    <Typography sx={{ color: BRAND.white, fontSize: 14, mb: 1, fontWeight: 700 }}>📥 Fetching Data (GET)</Typography>
                    <CodeBlock lines={[
                        { cmd: "app.get('/users/:userId', async (req, res) => {", comment: "Async Route" },
                        { cmd: "  try {", comment: "" },
                        { cmd: "    const user = await getUserFromDb(req.params.userId);", comment: "Wait for DB" },
                        { cmd: "    res.json({ success: true, user });", comment: "Success Response" },
                        { cmd: "  } catch (error) {", comment: "" },
                        { cmd: "    res.status(404).json({ success: false, message: error.message });", comment: "" },
                        { cmd: "  }", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />

                    <Typography sx={{ color: BRAND.white, fontSize: 14, mb: 1, mt: 3, fontWeight: 700 }}>📝 Creating Data (POST)</Typography>
                    <CodeBlock lines={[
                        { cmd: "app.post('/users', async (req, res) => {", comment: "Handle body data" },
                        { cmd: "  try {", comment: "" },
                        { cmd: "    const savedUser = await saveToDb(req.body);", comment: "" },
                        { cmd: "    res.status(201).json({ success: true, user: savedUser });", comment: "Created Status" },
                        { cmd: "  } catch (error) {", comment: "" },
                        { cmd: "    res.status(500).json({ success: false, message: 'Save Failed' });", comment: "" },
                        { cmd: "  }", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 3. ERROR HANDLING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<BugReportIcon />} label="3. Global Error Handling" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        A central error handler catches any unhandled exceptions, preventing your server from crashing and providing a unified JSON error format.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.use((err, req, res, next) => {", comment: "Global Error Middleware" },
                        { cmd: "  console.error(err.stack);", comment: "Log the trace" },
                        { cmd: "  res.status(500).json({ success: false, message: 'Server Error' });", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Learning Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Implement Async/Await within Express route handlers",
                            "Simulate and handle asynchronous Database interactions",
                            "Utilize Try/Catch blocks for elegant local error management",
                            "Distinguish between Params (URL) and Body (JSON) data",
                            "Deploy a centralized Global Error Handling middleware",
                            "Apply appropriate HTTP status codes (201, 404, 500)",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Don't forget <code style={{ color: BRAND.goldBright }}>app.use(express.json())</code>! Without this middleware, your POST requests will receive an <code style={{ color: BRAND.goldBright }}>undefined</code> request body.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
